import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  useMediaQuery,
  Stack,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getAPICall } from "lib/callApi";
import {
  GET_TROPHY_CABINET_CONTENT,
  GET_USER_TROPHY_CABINET_DATA,
  GET_CUSTOMIZE_BADGE_DATA,
} from "lib/constants";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import AutocompleteDark from "utils/AutocompleteDark";
import Badge from "./Badge";
import CustomizeBadgeModal from "./CustomizeBadgeModal";

const Cabinet = ({ showNotification }) => {
  const user = useSelector((state) => state.loginUser.loginUserState);
  const isMobileDisplay = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const theme = useTheme();
  const [badges, setBadges] = useState([]);
  const [userBadgeData, setUserBadgeData] = useState({
    userBadges: [],
    userActions: [],
    userRevenue: 0,
    userStreaks: { activeStreak: 0, longestStreak: 0 },
    leaderStreaks: { activeStreak: 0, longestStreak: 0 },
    teamStreaks: { totalMembers: 0, currentStreaks: 0 },
  });
  const [customizeBadgeData, setCustomizeBadgeData] = useState({
    customBadgeMap: {},
    companyKpis: [],
    companyUserBadges: [],
  });
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(user.userCompanyLinkId);
  const [showOnlyUserBadges, setShowOnlyUserBadges] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isCustomizable = user?.userPermission?.manage_kpis || false;

  useEffect(() => {
    const fetchInitialContent = async () => {
      const result = await getAPICall(GET_TROPHY_CABINET_CONTENT, true);
      if (result?.data?.success) {
        setUsers(result.data.data.userList);
        setBadges(result.data.data.badgeDefinitions);
      }
    };

    fetchInitialContent();
  }, []);

  useEffect(() => {
    const fetchUserBadgeData = async () => {
      const result = await getAPICall(GET_USER_TROPHY_CABINET_DATA, true, {
        userCompanyLinkId: selectedUser,
      });
      if (result?.data?.success) {
        setUserBadgeData(result.data.data);
      }
    };

    fetchUserBadgeData();
  }, [selectedUser]);

  const handleOpenModal = async () => {
    // fetch modal data
    const result = await getAPICall(GET_CUSTOMIZE_BADGE_DATA, true);
    if (result?.data?.success) {
      setCustomizeBadgeData(result.data.data);
    }
    setIsModalOpen(true);
  };

  // Function to determine if the user has a particular badge
  const userHasBadge = (badgeId) =>
    userBadgeData.userBadges.some((userBadge) => userBadge.FK_badge_definitions_id === badgeId);

  // Sort badges by group_order, then sort_order, and default to badge name if null
  const sortedBadges = badges.sort((a, b) => {
    if (a.group_order === null && b.group_order !== null) {
      return 1;
    }
    if (a.group_order !== null && b.group_order === null) {
      return -1;
    }
    if (a.group_order === b.group_order) {
      if (a.sort_order === b.sort_order) {
        return a.name.localeCompare(b.name);
      }
      return (a.sort_order || 0) - (b.sort_order || 0);
    }
    return (a.group_order || 0) - (b.group_order || 0);
  });

  const filteredBadges = showOnlyUserBadges
    ? sortedBadges.filter((badge) => userHasBadge(badge.id))
    : sortedBadges;

  // const groupedBadges = filteredBadges.reduce((acc, badge) => {
  //   const group = badge.group_order || "no_group";
  //   if (!acc[group]) {
  //     acc[group] = [];
  //   }
  //   acc[group].push(badge);
  //   return acc;
  // }, {});

  const handleUserChange = (event, newValue) => {
    if (newValue) {
      setSelectedUser(newValue.id);
    }
  };

  const handleCheckboxChange = (event) => {
    setShowOnlyUserBadges(event.target.checked);
  };

  const calculateDateDisplay = (badge) => {
    const usrBadge = userBadgeData.userBadges.find(
      (userBadge) => userBadge.FK_badge_definitions_id === badge.id
    );
    return usrBadge
      ? moment.tz(usrBadge.createdAt, user.timezone || "America/Denver").format("ddd MMM D, YYYY")
      : "";
  };

  const calculateGraphData = (badge) => {
    switch (badge.criteria_type) {
      case "kpi_count": {
        const { criteria_details: criteriaDetails } = badge;
        if (criteriaDetails && "kpiActionId" in criteriaDetails && "kpiCount" in criteriaDetails) {
          const { kpiCount } = criteriaDetails;
          if (userHasBadge(badge.id)) return { value: kpiCount, max: kpiCount };
          const { totalCount = 0 } =
            userBadgeData.userActions.find(
              (kpi) => kpi.kpiActionId === criteriaDetails.kpiActionId
            ) || {};
          return { value: Math.min(totalCount, kpiCount), max: kpiCount };
        }
        break;
      }
      case "streak": {
        const { FK_badge_scopes: badgeScope, criteria_details: criteriaDetails } = badge;
        if (badgeScope === "individual") {
          if (criteriaDetails && "streakDays" in criteriaDetails) {
            const { streakDays } = criteriaDetails;
            if (userHasBadge(badge.id)) return { value: streakDays, max: streakDays };
            return {
              value: Math.min(
                Math.max(
                  userBadgeData.userStreaks.longestStreak,
                  userBadgeData.userStreaks.activeStreak
                ),
                streakDays
              ),
              max: streakDays,
            };
          }
        } else {
          if (userHasBadge(badge.id))
            return {
              value: userBadgeData.teamStreaks.totalMembers,
              max: userBadgeData.teamStreaks.totalMembers,
            };
          return {
            value: userBadgeData.teamStreaks.currentStreaks,
            max: userBadgeData.teamStreaks.totalMembers,
          };
        }
        break;
      }
      case "streak_all_time": {
        const max = Math.max(
          userBadgeData.userStreaks.activeStreak,
          userBadgeData.userStreaks.longestStreak,
          userBadgeData.leaderStreaks.activeStreak,
          userBadgeData.leaderStreaks.longestStreak
        );
        return {
          value: Math.min(
            Math.max(
              userBadgeData.userStreaks.longestStreak,
              userBadgeData.userStreaks.activeStreak
            ),
            max
          ),
          max,
        };
      }
      case "revenue": {
        const { criteria_details: criteriaDetails } = badge;
        if (criteriaDetails && "revenue" in criteriaDetails) {
          const { revenue } = criteriaDetails;
          if (userHasBadge(badge.id)) return { value: revenue, max: revenue };
          return {
            value: Math.min(userBadgeData.userRevenue, revenue),
            max: revenue,
          };
        }
        break;
      }
      default:
        return { value: 0, max: 0 };
    }
    return { value: 0, max: 0 };
  };

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Stack justifyContent="space-between" direction="row">
          <Box
            display="flex"
            p={1}
            flexDirection={isMobileDisplay ? "column" : "row"}
            alignItems="baseline"
            mb={2}
          >
            <AutocompleteDark
              sx={{ ml: 2, width: "200px" }}
              options={users}
              getOptionLabel={(option) => option.name}
              value={users.find((u) => u.id === selectedUser) || null}
              onChange={handleUserChange}
              label="Show badges for"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyUserBadges}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: "#ddd9d5",
                    "& .MuiSvgIcon-root": {
                      borderColor: "#ddd9d5 !important",
                    },
                  }}
                />
              }
              label="Show only badges earned"
              sx={{
                whiteSpace: "nowrap",
                ml: 4,
                "& .MuiFormControlLabel-label": { color: "#ddd9d5" },
              }}
            />
          </Box>
          <Button
            onClick={handleOpenModal}
            disabled={!isCustomizable}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primaryRed.main,
              color: "#fff",
              borderRadius: "2rem",
              height: "1.5rem",
              mt: "1rem",
              "&:hover": {
                backgroundColor: theme.palette.primaryRed.main,
                cursor: "pointer",
              },
              "&:focus:not(:hover)": {
                outline: "none",
                backgroundColor: theme.palette.primaryRed.main,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.grey[800],
                color: theme.palette.grey[600],
              },
            }}
          >
            Customize Badges
          </Button>
        </Stack>
        {filteredBadges.length ? (
          <Grid container spacing={2} justifyContent="flex-start" wrap="wrap">
            {filteredBadges.map((badge) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={badge.id}>
                <Badge
                  badge={badge}
                  userHasBadge={userHasBadge(badge.id)}
                  graphData={calculateGraphData(badge)}
                  dateDisplay={calculateDateDisplay(badge)}
                />
              </Grid>
            ))}
            {/* {Object.keys(groupedBadges).map((group, index) => (
            <React.Fragment key={group}>
              {index > 0 && <Divider sx={{ my: 3, borderWidth: 2, borderColor: "#aaa" }} />}
              <Grid container spacing={2} justifyContent="flex-start" wrap="wrap">
                {groupedBadges[group].map((badge) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={badge.id}>
                    <Badge badge={badge} userHasBadge={userHasBadge(badge.id)} />
                  </Grid>
                ))}
              </Grid>
            </React.Fragment>
          ))} */}
          </Grid>
        ) : (
          <Typography sx={{ fontSize: "1rem", color: "#a5a4ab", ml: 4 }}>
            No badges to display
          </Typography>
        )}
      </Box>
      {isModalOpen && (
        <CustomizeBadgeModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen((prev) => !prev)}
          customizeBadgeData={customizeBadgeData}
          showNotification={showNotification}
          selectedUser={selectedUser}
          setBadges={setBadges}
          setUserBadgeData={setUserBadgeData}
        />
      )}
    </>
  );
};

export default Cabinet;
